import React from 'react';
import { Lock } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = new Date().toLocaleDateString();

  return (
    <div className="bg-black min-h-screen text-gray-300 pt-20 px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-12">
          <Lock className="w-16 h-16 mr-6 text-white" />
          <h1 className="text-5xl font-bold text-white tracking-tight">
            Privacy Policy
          </h1>
        </div>

        <div className="space-y-12">
          {/* Introduction */}
          <div className="bg-gray-900 p-6 rounded-lg">
            <p className="text-lg leading-relaxed">
              Intelligent System Design Pty Ltd ("we", "us", "our", or "Company") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
              ContextUI service, including our website and software applications (collectively, the "Service").
            </p>
            <p className="mt-4 text-lg leading-relaxed">
              Please read this Privacy Policy carefully. By accessing or using the Service, you acknowledge that you have 
              read, understood, and agree to be bound by this Privacy Policy. If you do not agree with our policies and 
              practices, please do not use our Service.
            </p>
          </div>

          {/* Information We Collect */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Information We Collect</h2>
            
            <p className="text-lg leading-relaxed mb-4">
              We collect several types of information from and about users of our Service:
            </p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Account Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Basic Account Information</strong>: When you register for an account, we collect your name, email address, and password.
                  </li>
                  <li>
                    <strong>Payment Information</strong>: When you subscribe to our paid services, we collect payment information, such as credit card details or other financial information. This information is processed by our payment processors and is not stored on our servers.
                  </li>
                  <li>
                    <strong>Profile Information</strong>: Information you provide in your user profile, such as your organization or job title.
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Limited Usage Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Service Usage Metrics</strong>: Basic information about how you use our Service, including login frequency and feature access patterns.
                  </li>
                  <li>
                    <strong>Error Reports</strong>: Information sent to us when the application encounters an error (with your consent).
                  </li>
                  <li>
                    <strong>Device Information</strong>: Limited information about your device when you download or update the application, such as operating system version.
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Public Content</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Shared Workflows</strong>: If you choose to publish or share workflows, models, or visualizations on our community platform, that information becomes public and can be accessed by other users.
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Local Data - Not Collected by Us</h3>
                <p className="text-lg leading-relaxed mb-2">
                  The following data remains on your local device and is <strong>not</strong> transmitted to or stored on our servers:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>AI Models</strong>: Any models you build, train, or analyze using our desktop application</li>
                  <li><strong>Model Weights</strong>: Weights, parameters, or other model configuration data</li>
                  <li><strong>Training Data</strong>: Any data you use to train models</li>
                  <li><strong>Visualization Data</strong>: Data generated when you use our visualization features to analyze AI architectures locally</li>
                  <li><strong>Analysis Results</strong>: Results of your analyses, interpretations, or insights generated locally</li>
                </ul>
                <p className="text-lg leading-relaxed mt-2">
                  We do not have access to data processed locally by the desktop application unless you explicitly choose to share it through our community platform.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">How We Use Your Information</h2>
            
            <p className="text-lg leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>Provide, maintain, and improve our Service</li>
              <li>Process your transactions and manage your account</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send you technical notices, updates, security alerts, and administrative messages</li>
              <li>Communicate with you about new features, products, or services</li>
              <li>Monitor and analyze general usage patterns to improve our desktop application</li>
              <li>Prevent fraudulent or unauthorized use of our Service</li>
              <li>Comply with legal obligations</li>
            </ul>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Local vs. Cloud Processing</h3>
                <p className="text-lg leading-relaxed mb-2">
                  ContextUI primarily processes data locally on your device:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Local Processing</strong>: Most computation, analysis, and visualization occurs locally on your device, meaning your data stays under your control</li>
                  <li><strong>No Cloud Storage</strong>: We do not automatically upload or store your models, weights, or analysis data on our servers</li>
                  <li><strong>Limited Collection</strong>: We only collect the specific account and usage information outlined in this policy</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Shared Content</h3>
                <p className="text-lg leading-relaxed mb-2">
                  If you choose to share workflows, models, or visualizations on our community platform:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You acknowledge that this content will be accessible to other users</li>
                  <li>You should not include sensitive or confidential information in shared content</li>
                  <li>You maintain ownership of your shared content, but grant us a license to display, distribute, and use it as part of our Service</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Share Your Information */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">How We Share Your Information</h2>
            
            <p className="text-lg leading-relaxed mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following limited circumstances:
            </p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Service Providers</h3>
                <p className="text-lg leading-relaxed">
                  We may share your information with third-party vendors, service providers, contractors, or agents who perform services on our behalf, such as:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>
                    <strong>Auth0</strong>: We use Auth0 (https://auth0.com) for authentication services and account management. When you create an account or log in, your authentication information is processed by Auth0 in accordance with their privacy policy.
                  </li>
                  <li>
                    <strong>Stripe</strong>: We use Stripe (https://stripe.com) for payment processing. When you make a payment, your payment information is provided directly to Stripe and is subject to their privacy policy. We do not store your full payment details on our servers.
                  </li>
                  <li>Cloud hosting providers for our website and community platform</li>
                  <li>Analytics providers to help us understand usage of our Service</li>
                  <li>Customer support tools to provide assistance</li>
                </ul>
                <p className="text-lg leading-relaxed mt-2">
                  For information about how these service providers process your data, we encourage you to review their respective privacy policies:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Auth0 Privacy Policy: <a href="https://auth0.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">https://auth0.com/privacy</a></li>
                  <li>Stripe Privacy Policy: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">https://stripe.com/privacy</a></li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Legal Requirements</h3>
                <p className="text-lg leading-relaxed">
                  We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Business Transfers</h3>
                <p className="text-lg leading-relaxed">
                  If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email and/or a prominent notice on our Service of any change in ownership or uses of your personal information.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">With Your Consent</h3>
                <p className="text-lg leading-relaxed">
                  We may share your information with third parties when you explicitly consent to such sharing.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Public Content</h3>
                <p className="text-lg leading-relaxed">
                  Any content you choose to make public through our community platform (such as shared workflows, models, or visualizations) will be accessible to other users of the Service. Please exercise caution when deciding what information to share publicly.
                </p>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Data Retention</h2>
            
            <p className="text-lg leading-relaxed mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
            </p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Specific Retention Periods</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Information</strong>: Retained as long as your account is active. After account closure, we retain basic information for up to 30 days before deletion.</li>
                  <li><strong>Payment Information</strong>: Transaction records are kept for 7 years for tax and accounting purposes, though full payment details are not retained.</li>
                  <li><strong>Usage Data</strong>: Basic application usage statistics are retained in aggregated form for up to 24 months.</li>
                  <li><strong>Communications</strong>: Customer support communications are retained for up to 24 months from the date of last communication.</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Data Deletion</h3>
                <p className="text-lg leading-relaxed">
                  When we no longer need your personal information, we will securely delete or anonymize it. You may request deletion of your account data at any time through your account settings or by contacting our support team.
                </p>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Data Security and User Control</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Desktop Application Security</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Local Data Storage</strong>: Data processed in our desktop application remains on your device unless you choose to share it</li>
                  <li><strong>User Control</strong>: You have full control over your local data, including the ability to delete it at any time</li>
                  <li><strong>Secure Communication</strong>: When the application communicates with our servers (e.g., for account authentication or update checks), we use industry-standard encryption protocols</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Account Security</h3>
                <p className="text-lg leading-relaxed mb-2">
                  We implement appropriate technical and organizational measures to protect your account information, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption of sensitive data in transit using TLS/SSL</li>
                  <li>Secure authentication mechanisms</li>
                  <li>Regular security assessments and updates</li>
                  <li>Limited employee access to personal information on a need-to-know basis</li>
                </ul>
                <p className="text-lg leading-relaxed mt-2">
                  However, please be aware that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Data Breach Response</h3>
                <p className="text-lg leading-relaxed mb-2">
                  In the event of a data breach that affects your personal information, we will:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Notify affected users promptly as required by applicable law</li>
                  <li>Provide information about what data was affected</li>
                  <li>Outline steps we're taking to address the breach</li>
                  <li>Offer guidance on protecting your information</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Your Responsibility</h3>
                <p className="text-lg leading-relaxed mb-2">
                  You are responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Maintaining the security of your device where the desktop application is installed</li>
                  <li>Protecting your account credentials</li>
                  <li>Being selective about what content you choose to share publicly on our community platform</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Your Privacy Rights */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Your Privacy Rights</h2>
            
            <p className="text-lg leading-relaxed mb-4">
              Depending on your location, you may have certain rights regarding your personal information, which may include:
            </p>
            
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li><strong>Access</strong>: The right to request copies of your personal information</li>
              <li><strong>Rectification</strong>: The right to request correction of information you believe is inaccurate</li>
              <li><strong>Erasure</strong>: The right to request deletion of your personal information in certain circumstances</li>
              <li><strong>Restriction</strong>: The right to request restriction of processing of your personal information</li>
              <li><strong>Data Portability</strong>: The right to receive a copy of your personal information in a structured, machine-readable format</li>
              <li><strong>Objection</strong>: The right to object to our processing of your personal information</li>
            </ul>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">How to Exercise Your Rights</h3>
                <p className="text-lg leading-relaxed mb-2">
                  To exercise these rights, please:
                </p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Log into your account settings, where many options for accessing and controlling your data are available</li>
                  <li>Contact us at <a href="mailto:support@isd.ai" className="text-blue-400 hover:text-blue-300">support@isd.ai</a> with your specific request</li>
                  <li>Verify your identity when requested (to protect your data from unauthorized access)</li>
                </ol>
                <p className="text-lg leading-relaxed mt-2">
                  We will respond to all legitimate requests within 30 days.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">California Residents</h3>
                <p className="text-lg leading-relaxed">
                  If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA), including:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>The right to know what personal information we collect and how it is used and shared</li>
                  <li>The right to delete personal information (with certain exceptions)</li>
                  <li>The right to opt-out of the sale of personal information (note that we do not sell personal information)</li>
                  <li>The right to non-discrimination for exercising your CCPA rights</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">European Economic Area (EEA) Residents</h3>
                <p className="text-lg leading-relaxed mb-2">
                  If you are located in the EEA, we comply with applicable data protection laws when transferring your personal information outside of the EEA. We ensure a similar degree of protection is afforded to your personal information through appropriate safeguards such as Standard Contractual Clauses approved by the European Commission.
                </p>
                <p className="text-lg leading-relaxed">
                  For EEA users, the legal basis for processing your information includes:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Performance of our contract with you</li>
                  <li>Compliance with legal obligations</li>
                  <li>Our legitimate interests (such as service improvement and security)</li>
                  <li>Your consent, where required</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Children's Privacy</h2>
            
            <p className="text-lg leading-relaxed">
              Our Service is not directed to children under the age of 16. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us so that we can take appropriate action.
            </p>
          </section>

          {/* Third-Party Services */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Third-Party Services and APIs</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">External Links and Services</h3>
                <p className="text-lg leading-relaxed">
                  Our Service may contain links to other websites or services that are not operated by us. This Privacy Policy does not apply to third-party websites or services. We recommend reviewing the privacy policies of any third-party websites or services that you visit.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">User-Initiated API Connections</h3>
                <p className="text-lg leading-relaxed mb-2">
                  ContextUI allows you to connect to third-party AI services (such as Anthropic, OpenAI, or other AI providers) using your own API keys. Regarding such connections:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>No Storage of API Keys</strong>: We do not permanently store your third-party API keys on our servers</li>
                  <li><strong>No Liability for Third-Party Processing</strong>: We have no control over and assume no responsibility for how these third-party services process, store, or use data you send to them through our application</li>
                  <li><strong>No Monitoring of Content</strong>: We do not monitor, log, or access the content of requests you make to third-party APIs or the responses you receive</li>
                  <li><strong>Third-Party Terms Apply</strong>: Your use of third-party APIs is subject to those services' own terms, privacy policies, and pricing</li>
                  <li><strong>No Control Over Outputs</strong>: We are not responsible for any outputs, content, or information generated by third-party AI services accessed through our application</li>
                  <li><strong>No Liability for Costs</strong>: You are responsible for any costs, charges, or usage fees incurred through your use of third-party API services</li>
                </ul>
                <p className="text-lg leading-relaxed mt-2">
                  You acknowledge that when you use third-party services through our application, the data you send may be processed according to those services' privacy practices, which are outside our control.
                </p>
              </div>
            </div>
          </section>

          {/* Cookies and Tracking Technologies */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Cookies and Tracking Technologies</h2>
            
            <p className="text-lg leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our Service and to enhance your experience. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
            </p>
          </section>

          {/* International Data Transfers */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">International Data Transfers</h2>
            
            <p className="text-lg leading-relaxed">
              Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where privacy laws may be different from those in your jurisdiction. If you are located outside Australia and choose to provide information to us, please note that we transfer the information, including personal information, to Australia and process it there.
            </p>
          </section>

          {/* Changes to This Privacy Policy */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Changes to This Privacy Policy</h2>
            
            <p className="text-lg leading-relaxed">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
          </section>

          {/* Institutional and Educational Users */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Institutional and Educational Users</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Special Considerations for Organizations</h3>
                <p className="text-lg leading-relaxed">
                  If you use ContextUI as part of an organization, institution, or educational establishment:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li><strong>Administrator Access</strong>: Your organization's administrator may have access to certain aspects of your account information</li>
                  <li><strong>Organizational Policies</strong>: Your use may be subject to your organization's internal data privacy and security policies in addition to this Privacy Policy</li>
                  <li><strong>Educational Use</strong>: For educational institutions, we take additional measures to protect student privacy in compliance with applicable education privacy laws</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Business Accounts</h3>
                <p className="text-lg leading-relaxed mb-2">
                  If you're using a ContextUI business or enterprise account:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Additional administrative and security features may be available to account administrators</li>
                  <li>Your organization may establish its own policies regarding storage, access, modification, deletion, sharing, and retention of information which may take precedence over this Privacy Policy</li>
                  <li>We are not responsible for the privacy or security practices of our customers, which may differ from those set forth in this Privacy Policy</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Data Processing Agreements</h3>
                <p className="text-lg leading-relaxed">
                  For organizations that require specific data processing terms to comply with regulations such as GDPR, we offer data processing agreements upon request. Please contact <a href="mailto:support@isd.ai" className="text-blue-400 hover:text-blue-300">support@isd.ai</a> for more information.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Us */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">Contact Us</h2>
            
            <p className="text-lg leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            
            <p className="text-lg mt-4">
              Intelligent System Design Pty Ltd<br />
              Email: <a href="mailto:support@isd.ai" className="text-blue-400 hover:text-blue-300">support@isd.ai</a>
            </p>
          </section>

          <div className="mt-16 text-center">
            <p className="text-sm text-gray-500 italic">
              Last Updated: {lastUpdated}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;